/**
 * MlsSchema — injects rich SoftwareApplication + Service + FAQPage
 * structured data specifically about MLS search, so Google surfaces
 * Central Rentals Canada when tenants + landlords search for:
 *
 *   • "MLS search Canada"
 *   • "MLS listings Ontario / BC / Quebec / Alberta / …"
 *   • "Property management MLS integration"
 *
 * Mounted on /mls (MlsListings page). Injects one JSON-LD script into
 * <head> and cleans up on unmount.
 */
import { useEffect } from "react";

const SCHEMA_ID = "crc-mls-jsonld";

const CANADIAN_PROVINCES = [
  { name: "Ontario",                     code: "ON" },
  { name: "Quebec",                      code: "QC" },
  { name: "British Columbia",            code: "BC" },
  { name: "Alberta",                     code: "AB" },
  { name: "Manitoba",                    code: "MB" },
  { name: "Saskatchewan",                code: "SK" },
  { name: "Nova Scotia",                 code: "NS" },
  { name: "New Brunswick",               code: "NB" },
  { name: "Newfoundland and Labrador",   code: "NL" },
  { name: "Prince Edward Island",        code: "PE" },
  { name: "Northwest Territories",       code: "NT" },
  { name: "Yukon",                       code: "YT" },
  { name: "Nunavut",                     code: "NU" },
];

const MLS_SOFTWARE_APP = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": "https://centralrentals.ca/mls#software",
  name: "Central Rentals Canada — MLS Search",
  alternateName: [
    "Central Rentals MLS search",
    "Canadian MLS property search",
    "MLS listings integration for Canadian landlords",
  ],
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "MLS Search & Property Management Software",
  operatingSystem: "Web, iOS, Android",
  url: "https://centralrentals.ca/mls",
  description:
    "Real-time MLS search across every Canadian province, integrated inside Central Rentals Canada. Landlords import listings straight into their portfolio, tenants browse verified rentals, and comparable-rent data feeds directly into the platform for market-rate suggestions. Powered by Repliers MLS.",
  featureList: [
    "Nation-wide MLS listing search — Ontario, Quebec, BC, Alberta and all territories",
    "One-click import of MLS listings into your Central Rentals portfolio",
    "Comparable-rent data for rent-analysis and market-rate suggestions",
    "PIPEDA-compliant Canadian data residency",
    "Filter by province, city, bedrooms, price, pet-friendly",
    "Deep links to CREA / MLS listing pages",
  ],
  offers: {
    "@type": "Offer",
    name: "Growth / Professional",
    price: "150.00",
    priceCurrency: "CAD",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: "150.00",
      priceCurrency: "CAD",
      unitCode: "MON",
      unitText: "per month, includes MLS search + import",
    },
    url: "https://centralrentals.ca/pricing",
    availability: "https://schema.org/InStock",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "42",
    bestRating: "5",
    worstRating: "1",
  },
  inLanguage: ["en-CA", "fr-CA"],
  audience: {
    "@type": "Audience",
    audienceType: "Canadian landlords, property managers, and rental tenants",
  },
  publisher: { "@id": "https://centralrentals.ca/#organization" },
};

// Service schema — one for each Canadian province so Central Rentals
// pops up on province-specific searches ("MLS Ontario", "MLS BC", etc.).
const MLS_SERVICES = CANADIAN_PROVINCES.map((p) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `https://centralrentals.ca/mls#service-${p.code}`,
  name: `MLS Search in ${p.name}`,
  serviceType: "Property Management Software with MLS Integration",
  provider: { "@id": "https://centralrentals.ca/#organization" },
  areaServed: {
    "@type": "AdministrativeArea",
    name: p.name,
    address: {
      "@type": "PostalAddress",
      addressCountry: "CA",
      addressRegion: p.code,
    },
  },
  url: `https://centralrentals.ca/mls?province=${p.code}`,
  description: `Search live MLS listings across ${p.name} and import them straight into Central Rentals Canada. Landlords get comparable-rent data, tenants browse verified rentals — all inside one dashboard.`,
  offers: {
    "@type": "Offer",
    priceCurrency: "CAD",
    price: "150.00",
    availability: "https://schema.org/InStock",
  },
  audience: {
    "@type": "Audience",
    audienceType: `${p.name} landlords, property managers, and rental tenants`,
  },
}));

const MLS_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://centralrentals.ca/mls#faq",
  mainEntity: [
    {
      "@type": "Question",
      name: "Can I search the Canadian MLS inside Central Rentals?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Central Rentals Canada ships a live MLS search across every Canadian province and territory. Search by city, province, price, or bedrooms and import selected listings straight into your portfolio with a single click. Powered by Repliers MLS + CREA feeds.",
      },
    },
    {
      "@type": "Question",
      name: "Does Central Rentals cover MLS listings in Ontario, Quebec, BC, and Alberta?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Central Rentals covers MLS listings in all 13 Canadian provinces and territories — Ontario, Quebec, British Columbia, Alberta, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, Newfoundland and Labrador, Prince Edward Island, Northwest Territories, Yukon, and Nunavut. Nation-wide coverage from a single dashboard.",
      },
    },
    {
      "@type": "Question",
      name: "How do I import an MLS listing into my rental portfolio?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Open Central Rentals Canada → MLS → search by province or city → click Import on any listing. The property, photos, rent estimate, and MLS reference are copied into your Central Rentals portfolio in about 3 seconds — ready to accept applications, run screening, or publish to the tenant marketplace.",
      },
    },
    {
      "@type": "Question",
      name: "Is Central Rentals MLS search PIPEDA-compliant?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Central Rentals Canada is fully PIPEDA-compliant with Canadian data residency. All MLS queries and imported listing data stay inside Canadian borders and are never sent to US-based providers.",
      },
    },
  ],
};

export default function MlsSchema() {
  useEffect(() => {
    document.getElementById(SCHEMA_ID)?.remove();
    const s = document.createElement("script");
    s.id = SCHEMA_ID;
    s.type = "application/ld+json";
    s.text = JSON.stringify([MLS_SOFTWARE_APP, ...MLS_SERVICES, MLS_FAQ]);
    document.head.appendChild(s);

    // Also tune the page title + meta description so search snippets
    // are unambiguous about what /mls does. Idempotent — restored on unmount.
    const prevTitle = document.title;
    document.title = "MLS Search — Canadian Property Management Software · Central Rentals Canada";
    const setMeta = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      const created = !el;
      if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
      const prevAttrs = {};
      Object.keys(attrs).forEach((k) => { prevAttrs[k] = el.getAttribute(k); });
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return { el, created, prevAttrs };
    };
    const metas = [
      setMeta('meta[name="description"]', { name: "description", content: "Search live MLS listings across all 13 Canadian provinces and import them straight into your rental portfolio. Central Rentals Canada — MLS search + property management software, PIPEDA-compliant." }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/mls" }),
      setMeta('meta[property="og:title"]', { property: "og:title", content: "MLS Search — Canadian Property Management Software" }),
      setMeta('meta[property="og:description"]', { property: "og:description", content: "Live MLS listings in every Canadian province, imported into your portfolio with one click." }),
      setMeta('meta[property="og:url"]', { property: "og:url", content: "https://centralrentals.ca/mls" }),
      setMeta('meta[name="keywords"]', { name: "keywords", content: "MLS search Canada, Canadian property management software, MLS Ontario, MLS Quebec, MLS BC, MLS Alberta, real estate listings Canada, property manager MLS, Central Rentals Canada" }),
    ];

    return () => {
      document.getElementById(SCHEMA_ID)?.remove();
      document.title = prevTitle;
      metas.forEach(({ el, created, prevAttrs }) => {
        if (created) { el.remove(); return; }
        Object.entries(prevAttrs).forEach(([k, v]) => {
          if (v === null) el.removeAttribute(k); else el.setAttribute(k, v);
        });
      });
    };
  }, []);
  return null;
}
